import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import {
  publishTimingChoices,
  releaseDayStart,
  type OneOffChannel,
  type PublishTiming,
} from 'fym-shared/src/music24/oneOffPublish';
import { isIanaTimeZone, zonedMidnight } from 'fym-shared/src/music24/timeZones';
import { TypedMemo } from '../../../common/types';
import { useArtistTimeZone } from '../../../settings/tabs/artist/timeZone/useArtistTimeZone';
import { publishMessages } from './publishMessages';

export interface PublishTimingValue {
  timing: PublishTiming;
  /** The `datetime-local` value of "Later" (local time). Empty until the user picks. */
  pickedAt: string;
}

/**
 * The start of the release day: 00:00 in the artist time zone (Settings → Artist → Time zone),
 * else in the time zone of this browser.
 */
export function localReleaseStart(releaseDate: string | null, timeZone: string | null = null): Date | null {
  if (timeZone !== null && releaseDate !== null && isIanaTimeZone(timeZone)) {
    return zonedMidnight(releaseDate, timeZone);
  }
  const day = releaseDayStart(releaseDate);
  return day === null ? null : releaseDayStart(releaseDate, new Date(day).getTimezoneOffset());
}

/**
 * The ISO time that the request sends: "On release day" = 00:00 of the release day in the artist
 * time zone (else here), "Later" = the picked local time, "Now" = none. Pure apart from the time
 * zone of the browser.
 */
export function publishAtOfValue(
  value: PublishTimingValue,
  releaseDate: string | null,
  timeZone: string | null = null
): string | null {
  if (value.timing === 'releaseDay') {
    return localReleaseStart(releaseDate, timeZone)?.toISOString() ?? null;
  }
  if (value.timing === 'later' && value.pickedAt !== '') {
    const at = new Date(value.pickedAt);
    return Number.isNaN(at.getTime()) ? null : at.toISOString();
  }
  return null;
}

interface PublishTimingFieldProps {
  channel: OneOffChannel;
  releaseDate: string | null;
  value: PublishTimingValue;
  onChange: (value: PublishTimingValue) => void;
  /** Unique per form: the radio group name. */
  name: string;
}

/**
 * "When does it go public?" (owner decision 2026-10-09): Now, On release day (the default while
 * the day is ahead; hidden when it is past), or Later with a date and time.
 */
const PublishTimingFieldComponent: React.FC<PublishTimingFieldProps> = ({
  channel,
  releaseDate,
  value,
  onChange,
  name,
}) => {
  const intl = useIntl();
  const timeZone = useArtistTimeZone();
  const choices = useMemo(
    () => publishTimingChoices(channel, localReleaseStart(releaseDate, timeZone), new Date()),
    [channel, releaseDate, timeZone]
  );
  const releaseText =
    releaseDate === null
      ? ''
      : intl.formatDate(new Date(`${releaseDate}T00:00:00`), { day: 'numeric', month: 'short', year: 'numeric' });
  const label = (timing: PublishTiming): string =>
    timing === 'now'
      ? intl.formatMessage(publishMessages.timingNow)
      : timing === 'releaseDay'
        ? intl.formatMessage(publishMessages.timingReleaseDay, { date: releaseText })
        : intl.formatMessage(publishMessages.timingLater);
  const changePicked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange({ timing: 'later', pickedAt: event.target.value }),
    [onChange]
  );

  return (
    <fieldset className="d-flex flex-column gap-2" data-testid="publish-timing">
      <legend className="fs-6 fw-semibold mb-1">{intl.formatMessage(publishMessages.promptWhen)}</legend>
      {choices.map((timing) => (
        <div className="form-check" key={timing}>
          <Input
            id={`${name}-${timing}`}
            type="radio"
            name={name}
            className="form-check-input"
            checked={value.timing === timing}
            onChange={() => onChange({ ...value, timing })}
          />
          <label className="form-check-label" htmlFor={`${name}-${timing}`}>
            {label(timing)}
          </label>
        </div>
      ))}
      {value.timing === 'later' && (
        <Input
          type="datetime-local"
          aria-label={intl.formatMessage(publishMessages.timingPick)}
          value={value.pickedAt}
          onChange={changePicked}
          data-testid="publish-picked-at"
        />
      )}
    </fieldset>
  );
};

export const PublishTimingField = TypedMemo(PublishTimingFieldComponent);
